// JSON-LD structured-data builders. Feeds both classic search-engine rich
// results and GEO (AI answer-engine) citations — clear entity + Q&A markup
// is what lets tools like Google AI Overviews / ChatGPT / Perplexity cite
// Cabil accurately instead of guessing from unstructured page text.

const SITE_URL = "https://cabil.co";
const LOGO_URL = `${SITE_URL}/logo.png`;
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.cabil.kids";

/** The parent Cabil organization — mentorship + Cabil Kids under one brand. */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Cabil",
    "legalName": "Cabil Educational Services",
    "url": SITE_URL,
    "logo": LOGO_URL,
    "description": "Karachi-founded EdTech company behind Cabil Kids, a safe, ad-free learning app for children ages 4-12, and a mentorship platform connecting students and professionals with expert mentors.",
    "foundingLocation": { "@type": "Place", "name": "Karachi, Pakistan" },
    "areaServed": ["PK", "AE", "SA", "QA", "GB", "US", "CA"],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Office 404, Al-Fiza Glass Tower, Block 10-A, Rashid Minhas Road",
      "addressLocality": "Karachi",
      "addressCountry": "PK",
    },
    "sameAs": [
      PLAY_STORE_URL,
      "https://www.facebook.com/share/1Ap7ra6r6r/",
      "https://www.linkedin.com/company/cabil/",
      "https://www.instagram.com/cabil_career_crew",
    ],
  };
}

/** WebSite schema with a SearchAction — enables the sitelinks search box. */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Cabil",
    "url": SITE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${SITE_URL}/blog?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

/** Cabil Kids as its own product/service entity — what "games Cabil" searches should resolve to. */
export function kidsProductSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Cabil Kids",
    "description": "Learning games app for kids ages 4-12: math, English, science, Urdu, Arabic and coding games, an AI study buddy, AR Point & Learn, and weekly parent reports — safe, ad-free, and parent-controlled.",
    "brand": { "@type": "Brand", "name": "Cabil" },
    "url": `${SITE_URL}/kids`,
    "image": LOGO_URL,
    "offers": {
      "@type": "Offer",
      "url": `${SITE_URL}/kids/plans`,
      "priceCurrency": "PKR",
      "availability": "https://schema.org/InStock",
    },
  };
}

/** The Android app — lets search and AI answer engines link "Cabil Kids app" straight to Google Play. */
export function mobileAppSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": "Cabil Kids",
    "operatingSystem": "ANDROID",
    "applicationCategory": "EducationalApplication",
    "description": "Safe, ad-free learning games for kids ages 4-12 with an AI study buddy, AR Point & Learn and parent progress reports.",
    "url": `${SITE_URL}/kids`,
    "downloadUrl": PLAY_STORE_URL,
    "installUrl": PLAY_STORE_URL,
    "image": LOGO_URL,
    "audience": { "@type": "PeopleAudience", "suggestedMinAge": 4, "suggestedMaxAge": 12 },
    "publisher": { "@type": "Organization", "name": "Cabil", "url": SITE_URL },
  };
}

/** FAQPage schema — build from an array of { q, a } pairs. */
export function faqSchema(pairs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": pairs.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a },
    })),
  };
}

/** BreadcrumbList schema — helper for deeper pages (e.g. blog posts). */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`,
    })),
  };
}

/** BlogPosting schema for individual blog posts. */
export function blogPostingSchema({ title, description, path, image, datePublished, author }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "url": `${SITE_URL}${path}`,
    "image": image || LOGO_URL,
    "datePublished": datePublished,
    "author": { "@type": "Organization", "name": author || "Cabil" },
    "publisher": {
      "@type": "Organization",
      "name": "Cabil",
      "logo": { "@type": "ImageObject", "url": LOGO_URL },
    },
  };
}
